/**
 * Utility functions for social media components
 *
 * These utilities help extract and format data from PlatinumData
 * for use in social media visualization components.
 */

import type { PlatinumData } from '../../types/carbon/models/PlatinumData'

export type CellType = 'freq' | 'colPct' | 'rowPct';

/**
 * Find the index of a row by its label
 *
 * @param data - The PlatinumData object
 * @param label - The row label to find
 * @returns The row index, or -1 if not found
 */
export function findRowIndex(data: PlatinumData, label: string): number {
  const sideAxis = data.side
  if (!sideAxis || !sideAxis.vecs) return -1

  return sideAxis.vecs.findIndex((vec) => {
    const vecLabel = vec.label || ''
    const vecType = vec.type || ''

    // Handle "Weighted Total" mapping
    if (vecType.toLowerCase() === 'base' && vecLabel === 'Total') {
      return label === 'Weighted Total' || label === 'Total'
    }

    return vecLabel === label
  })
}

/**
 * Find the index of a column by its label
 *
 * @param data - The PlatinumData object
 * @param label - The column label to find
 * @returns The column index, or -1 if not found
 */
export function findColumnIndex(data: PlatinumData, label: string): number {
  const topAxis = data.top
  if (!topAxis || !topAxis.vecs) return -1

  return topAxis.vecs.findIndex((vec) => (vec.label || '') === label)
}

/**
 * Extract a cell value from PlatinumData
 *
 * @param data - The PlatinumData object
 * @param row - Row label or index
 * @param column - Column label or index
 * @param cellType - Which value to extract: 'freq', 'colPct', or 'rowPct'
 * @returns The cell value, or null if not found
 */
export function getCellValue(
  data: PlatinumData,
  row: string | number,
  column: string | number,
  cellType: CellType
): number | null {
  const rowIndex = typeof row === 'string' ? findRowIndex(data, row) : row
  const colIndex = typeof column === 'string' ? findColumnIndex(data, column) : column

  if (rowIndex < 0 || colIndex < 0) return null

  const rows = data.cells?.rows
  if (!rows || rowIndex >= rows.length) return null

  const cellArray = rows[rowIndex]?.cell
  if (!cellArray || colIndex >= cellArray.length) return null

  const cell = cellArray[colIndex]
  if (!cell) return null

  switch (cellType) {
    case 'freq':
      return cell.freq ?? null
    case 'colPct':
      return cell.colpc ?? null
    case 'rowPct':
      return cell.rowpc ?? null
    default:
      return null
  }
}

/**
 * Get the first data column (skip Base/Total columns)
 *
 * @param data - The PlatinumData object
 * @returns The label of the first data column, or null if none found
 */
export function getFirstDataColumn(data: PlatinumData): string | null {
  const topAxis = data.top
  if (!topAxis || !topAxis.vecs) return null

  // First try to find a non-Base column
  for (const vec of topAxis.vecs) {
    const vecType = (vec.type || '').toLowerCase()
    if (vecType !== 'base') {
      return vec.label || null
    }
  }

  // Fallback to first column if all are Base
  return topAxis.vecs[0]?.label || null
}

/**
 * Format a cell value for display
 *
 * @param value - The numeric value
 * @param cellType - The type of value
 * @returns Formatted string
 */
export function formatCellValue(value: number | null, cellType: CellType): string {
  if (value === null || value === undefined) return '-'

  switch (cellType) {
    case 'freq':
      return Math.round(value).toLocaleString()
    case 'colPct':
    case 'rowPct':
      return `${value.toFixed(1)}%`
    default:
      return String(value)
  }
}

/**
 * Extract all row labels from PlatinumData
 *
 * @param data - The PlatinumData object
 * @returns Array of row labels
 */
export function getRowLabels(data: PlatinumData): string[] {
  const sideAxis = data.side
  if (!sideAxis || !sideAxis.vecs) return []

  return sideAxis.vecs.map((vec) => {
    const label = vec.label || ''
    const vecType = vec.type || ''

    // If the vec type is Base and label is "Total", change to "Weighted Total"
    if (vecType.toLowerCase() === 'base' && label === 'Total') {
      return 'Weighted Total'
    }

    return label
  })
}

/**
 * Extract all column labels from PlatinumData
 *
 * @param data - The PlatinumData object
 * @returns Array of column labels
 */
export function getColumnLabels(data: PlatinumData): string[] {
  const topAxis = data.top
  if (!topAxis || !topAxis.vecs) return []

  return topAxis.vecs.map((vec) => vec.label || '')
}

/**
 * Get row labels excluding Base types
 *
 * @param data - The PlatinumData object
 * @returns Array of non-Base row labels
 */
export function getNonBaseRowLabels(data: PlatinumData): string[] {
  const sideAxis = data.side
  if (!sideAxis || !sideAxis.vecs) return []

  return sideAxis.vecs
    .filter((vec) => (vec.type || '').toLowerCase() !== 'base')
    .map((vec) => vec.label || '')
}

/**
 * Get column labels excluding Base types
 *
 * @param data - The PlatinumData object
 * @returns Array of non-Base column labels
 */
export function getNonBaseColumnLabels(data: PlatinumData): string[] {
  const topAxis = data.top
  if (!topAxis || !topAxis.vecs) return []

  return topAxis.vecs
    .filter((vec) => (vec.type || '').toLowerCase() !== 'base')
    .map((vec) => vec.label || '')
}

/**
 * Check if the PlatinumData has valid structure for rendering
 *
 * @param data - The PlatinumData object to validate
 * @returns true if data is valid
 */
export function isValidPlatinumData(data: PlatinumData | null | undefined): data is PlatinumData {
  if (!data) return false
  if (!data.top?.vecs?.length) return false
  if (!data.side?.vecs?.length) return false
  if (!data.cells?.rows?.length) return false
  return true
}

/**
 * Get the 'Yes' percentage from a PlatinumData object
 * Searches for a 'yes'/'true'/'1' row and returns its column percentage
 */
export function getYesPercentage(data: PlatinumData | null | undefined): number | null {
  if (!data) return null

  const rowLabels = getRowLabels(data)

  // Try multiple representations of "yes/true"
  const yesVariants = ['yes', 'true', '1', 'y']
  const yesIndex = rowLabels.findIndex(label => {
    const normalized = label.toLowerCase().trim()
    return yesVariants.includes(normalized)
  })

  if (yesIndex === -1) {
    // If no yes/true row found, try to get the first non-base row
    const nonBaseIndex = rowLabels.findIndex(label =>
      !['weighted total', 'total', 'base'].includes(label.toLowerCase())
    )
    if (nonBaseIndex !== -1) {
      const cell = data.cells?.rows?.[nonBaseIndex]?.cell?.[0]
      if (cell?.colpc !== undefined) return cell.colpc
    }
    return null
  }

  const cell = data.cells?.rows?.[yesIndex]?.cell?.[0]
  if (!cell) return null

  return cell.colpc ?? null
}

/**
 * Get one row's column percentage from a PlatinumData object, by label.
 *
 * Case-insensitive match against the row labels (e.g. 'positive' matches a
 * "Positive" row), and the SAME colpc handling as {@link getYesPercentage}:
 * `cell.colpc` is returned as-is — a fraction (0–1), not already ×100 — so a
 * caller formats it the same way `getYesPercentage`'s callers do
 * (`${(v * 100).toFixed(1)}%`). Unlike `getYesPercentage` there is no
 * "first non-base row" fallback: the caller names the exact row it wants, and
 * a row that is not present is not an ambiguous case to guess at.
 */
export function getRowPercentage(data: PlatinumData | null | undefined, label: string): number | null {
  if (!data) return null

  const normalized = label.toLowerCase().trim()
  const rowLabels = getRowLabels(data)
  const rowIndex = rowLabels.findIndex((rowLabel) => rowLabel.toLowerCase().trim() === normalized)

  if (rowIndex === -1) return null

  const cell = data.cells?.rows?.[rowIndex]?.cell?.[0]
  if (!cell) return null

  return cell.colpc ?? null
}

/**
 * Result of the cultural relevance score calculation.
 *
 * Since EN-T20 this is a *response* shape, not a locally computed one: the
 * min-max normalization used to run in the browser over the filtered pipeline
 * page and now lives behind `POST /entities/cultural-relevance` (entities plan
 * part 6 §3), whose JSON mirrors this interface field for field. The old
 * `calculateCulturalRelevanceScore` / `CulturalRelevanceInput` pair was deleted
 * with the client-side maths; a vendored copy lives in
 * `scripts/janitor/entity-parity/vendor/` for the EN-T21 parity harness.
 */
export interface CulturalRelevanceResult {
  normComp: number | null
  standardAvg: number | null
  components: { me: number | null; md: number | null; ms: number | null }
  videoCount: number
}
